import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container, Typography, Box, Grid, Card, CardMedia, CardContent,
    Button, ThemeProvider, createTheme, CssBaseline,
    Dialog, DialogContent, IconButton, Chip, Divider, Snackbar,
    FormControl, Select, MenuItem, InputLabel
} from '@mui/material';
import { styled } from '@mui/system';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import Cart from "../Cart";
import API_URL from '../apiConfig';
import { useNavigate } from 'react-router-dom';

const defaultTheme = createTheme({
    palette: {
        primary: { main: '#e88ba9' },
        background: { default: '#fffafb' },
        text: { primary: '#5c434a', secondary: '#8c7077' },
    },
    typography: {
        fontFamily: '"Outfit", "Inter", sans-serif',
        h3: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 800 },
        h4: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 700 },
        h5: { fontFamily: '"Playfair Display", serif', color: '#d46a89', fontWeight: 600 },
    },
});

const BakeryButton = styled(Button)({
    background: '#e88ba9',
    borderRadius: '30px',
    border: 0,
    color: 'white',
    padding: '8px 24px',
    boxShadow: '0 8px 16px rgba(232, 139, 169, 0.4)',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '0.95rem',
    transition: 'all 0.3s ease',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 20px rgba(212, 106, 137, 0.6)', background: '#d46a89' },
    '&:active': { transform: 'translateY(0)' }
});

const OutlineButton = styled(Button)({
    borderRadius: '30px',
    color: '#d46a89',
    padding: '8px 24px',
    fontWeight: 700,
    textTransform: 'none',
    border: '2px solid #e88ba9',
    transition: 'all 0.3s ease',
    '&:hover': { color: 'white', backgroundColor: '#e88ba9', transform: 'translateY(-2px)', boxShadow: '0 6px 15px rgba(232, 139, 169, 0.4)' },
    '&:active': { transform: 'translateY(0)' }
});


const QtyButton = styled(IconButton)({
    backgroundColor: '#fffafb',
    border: '1px solid #fcf4f4',
    color: '#d46a89',
    '&:hover': { backgroundColor: '#fcf4f4' }
});

const categories = [
    'Individual Cupcake',
    'Cupcake Packs',
    'Bento Cake',
    'Cake',
    'Build Your Own Cake',
];

const priceRanges = [
    { label: 'All', value: '' },
    { label: 'LKR.0 - LKR.500', value: [0, 500] },
    { label: 'LKR.500 - LKR.1000', value: [500, 1000] },
    { label: 'LKR.1000 - LKR.2000', value: [1000, 2000] },
    { label: 'LKR.2000 - LKR.5000', value: [2000, 5000] },
    { label: 'LKR.5000+', value: [5000, Infinity] },
];

const INITIAL_VISIBLE = 4;

const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    const match = priceStr.toString().replace(/,/g, '').match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : 0;
};

const Menu = () => {
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedPriceRange, setSelectedPriceRange] = useState('');
    const [cakes, setCakes] = useState([]);
    const [selectedCake, setSelectedCake] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
    const [selectedCardIndex, setSelectedCardIndex] = useState(0);
    const [showMore, setShowMore] = useState({});
    const [snack, setSnack] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`${API_URL}/api/cakes`)
            .then(res => setCakes(res.data))
            .catch(err => console.error('Error fetching cakes:', err));
    }, []);

    const filteredCakes = cakes.filter((cake) => {
        const categoryMatch = selectedCategory ? cake.category === selectedCategory : true;
        let priceMatch = true;
        if (selectedPriceRange && selectedPriceRange.length === 2 && cake.price) {
            const priceNum = parsePrice(cake.price);
            if (!isNaN(priceNum)) {
                priceMatch = priceNum >= selectedPriceRange[0] && priceNum <= selectedPriceRange[1];
            }
        }
        return categoryMatch && priceMatch;
    }).sort((a, b) => {
        const priceA = parsePrice(a.price);
        const priceB = parsePrice(b.price);
        return priceA - priceB;
    });

    const handleOpenPopup = (cake) => {
        setSelectedCake(cake);
        setQuantity(1);
        setSelectedSizeIndex(0);
        setSelectedCardIndex(0);
    };

    const handleClosePopup = () => {
        setSelectedCake(null);
    };

    const handleAddToCart = () => {
        if (selectedCake) {
            let activePrice = parsePrice(selectedCake.price);
            let optionsText = [];

            if (selectedCake.sizes && selectedCake.sizes.length > 0) {
                const s = selectedCake.sizes[selectedSizeIndex];
                activePrice += s?.price || 0;
                optionsText.push(`Size: ${s?.label}`);
            }
            if (selectedCake.cardOptions && selectedCake.cardOptions.length > 0) {
                const c = selectedCake.cardOptions[selectedCardIndex];
                activePrice += c?.price || 0;
                if (c?.price > 0) optionsText.push(`Card: ${c?.label}`);
            }

            const cartItem = {
                ...selectedCake,
                price: `LKR.${activePrice.toFixed(2)}`,
                name: optionsText.length > 0 ? `${selectedCake.name} (${optionsText.join(', ')})` : selectedCake.name
            };

            Cart.addCake(cartItem, quantity);
            setSnack(`🧁 ${selectedCake.name} added to cart!`);
            handleClosePopup();
        }
    };

    const toggleShowMore = (category) => {
        setShowMore(prev => ({ ...prev, [category]: !prev[category] }));
    };

    return (
        <ThemeProvider theme={defaultTheme}>
            <Box sx={{ background: '#fffafb', minHeight: '100vh', pt: '100px', pb: 8 }}>
                <CssBaseline />
                <Container maxWidth="lg">

                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <RestaurantMenuIcon sx={{ fontSize: 48, color: '#e88ba9', mb: 2, opacity: 0.8 }} />
                        <Typography variant="h3" component="h1" gutterBottom>
                            Our Heavenly Menu
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#8c7077', maxWidth: '600px', mx: 'auto', fontSize: '1.1rem' }}>
                            Explore our delicious selection of handcrafted cakes and sweet treats, baked fresh every day with the finest ingredients.
                        </Typography>
                    </Box>

                    {/* Filters — Elegant Dropdowns */}
                    <Box sx={{ mb: 6, p: 3, background: '#fff', borderRadius: '24px', border: '1px solid #fcf4f4', boxShadow: '0 10px 30px rgba(232, 139, 169, 0.08)' }}>
                        <Grid container spacing={3}>
                            {/* Category filter */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ color: '#8c7077', '&.Mui-focused': { color: '#e88ba9' } }}>Filter by Category</InputLabel>
                                    <Select
                                        value={selectedCategory}
                                        label="Filter by Category"
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                        sx={{
                                            borderRadius: '16px',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(232,139,169,0.3)' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e88ba9' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e88ba9', borderWidth: '2px' },
                                            color: '#5c434a',
                                            fontWeight: 600
                                        }}
                                    >
                                        {[{ label: 'All Categories', value: '' }, ...categories.map(c => ({ label: c, value: c }))].map(item => (
                                            <MenuItem key={item.label} value={item.value} sx={{ fontWeight: 500, color: '#5c434a' }}>
                                                {item.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Price filter */}
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel sx={{ color: '#8c7077', '&.Mui-focused': { color: '#e88ba9' } }}>Filter by Price</InputLabel>
                                    <Select
                                        value={selectedPriceRange === '' ? '' : JSON.stringify(selectedPriceRange)}
                                        label="Filter by Price"
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === '') {
                                                setSelectedPriceRange('');
                                            } else {
                                                try {
                                                    setSelectedPriceRange(JSON.parse(val));
                                                } catch (err) {
                                                    setSelectedPriceRange('');
                                                }
                                            }
                                        }}
                                        sx={{
                                            borderRadius: '16px',
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(232,139,169,0.3)' },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e88ba9' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e88ba9', borderWidth: '2px' },
                                            color: '#5c434a',
                                            fontWeight: 600
                                        }}
                                    >
                                        <MenuItem key="all" value="" sx={{ fontWeight: 500, color: '#5c434a' }}>
                                            All Prices
                                        </MenuItem>
                                        {priceRanges.map(r => (
                                            <MenuItem key={r.label} value={r.value === '' ? '' : JSON.stringify(r.value)} sx={{ fontWeight: 500, color: '#5c434a' }}>
                                                {r.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Categories */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {categories.map((category) => {
                            const cakesInCategory = filteredCakes.filter(c => c.category === category);
                            if (cakesInCategory.length === 0 && category !== 'Build Your Own Cake') return null;
                            if (selectedCategory !== '' && selectedCategory !== category) return null;

                            const isExpanded = showMore[category];
                            const visible = isExpanded ? cakesInCategory : cakesInCategory.slice(0, INITIAL_VISIBLE);
                            const hasMore = cakesInCategory.length > INITIAL_VISIBLE;

                            return (
                                <Box key={category}>
                                    <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="h4" component="h2">
                                            {category}
                                        </Typography>
                                        <Box sx={{ flexGrow: 1, height: '1px', background: 'linear-gradient(90deg, #fcf4f4 0%, rgba(252,244,244,0) 100%)' }} />
                                    </Box>

                                    {category === 'Build Your Own Cake' ? (
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Card elevation={0} sx={{
                                                    height: '100%', display: 'flex', flexDirection: 'column',
                                                    borderRadius: '24px', border: '1px solid #fcf4f4', background: 'linear-gradient(135deg, #fffafb 0%, #ffedf4 100%)',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-8px)',
                                                        boxShadow: '0 20px 40px rgba(232, 139, 169, 0.2)',
                                                        borderColor: '#e88ba9'
                                                    }
                                                }} onClick={() => navigate('/customizeCake')}>
                                                    <Box sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                                        <Box sx={{
                                                            width: 80, height: 80, borderRadius: '50%', background: '#fff',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3,
                                                            boxShadow: '0 10px 20px rgba(232, 139, 169, 0.15)'
                                                        }}>
                                                            <Typography sx={{ fontSize: '2.5rem' }}>✨</Typography>
                                                        </Box>
                                                        <Typography variant="h5" sx={{ mb: 2, color: '#d46a89' }}>
                                                            Design Your Dream Cake
                                                        </Typography>
                                                        <Typography sx={{ color: '#8c7077', fontSize: '0.95rem', mb: 3 }}>
                                                            Choose your flavors, size, layers, and toppings to create a unique digital masterpiece!
                                                        </Typography>
                                                        <BakeryButton onClick={(e) => { e.stopPropagation(); navigate('/customizeCake'); }}>
                                                            Start Building
                                                        </BakeryButton>
                                                    </Box>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <>
                                            {/* Cake Cards – always 4 per row */}
                                            <Grid container spacing={3}>
                                                {visible.map((cake, index) => (
                                                    <Grid item xs={6} sm={3} key={cake._id || index}>
                                                        <Card elevation={0} sx={{
                                                            height: '100%', display: 'flex', flexDirection: 'column',
                                                            borderRadius: '24px', border: '1px solid #fcf4f4', background: '#fff',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                transform: 'translateY(-8px)',
                                                                boxShadow: '0 20px 40px rgba(232, 139, 169, 0.15)',
                                                                borderColor: 'rgba(232, 139, 169, 0.3)'
                                                            }
                                                        }} onClick={() => handleOpenPopup(cake)}>
                                                            <Box sx={{ p: 1.5, pb: 0 }}>
                                                                <CardMedia
                                                                    component="img"
                                                                    height="180"
                                                                    image={cake.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=800'}
                                                                    alt={cake.name}
                                                                    sx={{ objectFit: 'cover', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
                                                                />
                                                            </Box>
                                                            <CardContent sx={{ p: 2, pb: '8px !important', flexGrow: 1 }}>
                                                                <Typography sx={{ fontWeight: 700, color: '#5c434a', fontSize: '0.95rem', mb: 0.5, lineHeight: 1.3 }}>
                                                                    {cake.name}
                                                                </Typography>
                                                                <Typography sx={{ color: '#d46a89', fontWeight: 800, fontSize: '0.9rem' }}>
                                                                    {cake.price}
                                                                </Typography>
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>

                                            {/* Show More / Show Less */}
                                            {hasMore && (
                                                <Box sx={{ textAlign: 'center', mt: 4 }}>
                                                    <OutlineButton onClick={() => toggleShowMore(category)}>
                                                        {isExpanded ? `Show Less` : `Show More`}
                                                    </OutlineButton>
                                                </Box>
                                            )}
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Container>
            </Box>

            {/* ========= CAKE DETAIL POPUP ========= */}
            <Dialog
                open={Boolean(selectedCake)}
                onClose={handleClosePopup}
                maxWidth="md"
                fullWidth
                sx={{
                    '& .MuiBackdrop-root': {
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(92, 67, 74, 0.35)',
                    }
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '32px',
                        overflow: 'hidden',
                        boxShadow: '0 30px 80px rgba(212, 106, 137, 0.25)'
                    }
                }}
            >
                {selectedCake && (
                    <DialogContent sx={{ p: 0 }}>
                        <Grid container>
                            {/* Image */}
                            <Grid item xs={12} md={5} sx={{ position: 'relative', minHeight: { xs: '260px', md: '460px' } }}>
                                <Box
                                    component="img"
                                    src={selectedCake.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=800'}
                                    alt={selectedCake.name}
                                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </Grid>

                            {/* Details */}
                            <Grid item xs={12} md={7}>
                                <Box sx={{ p: { xs: 3, md: 5 }, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    {/* Close Button */}
                                    <IconButton
                                        onClick={handleClosePopup}
                                        sx={{ position: 'absolute', top: 16, right: 16, bgcolor: '#fffafb', '&:hover': { bgcolor: '#fcf4f4' } }}
                                    >
                                        <CloseIcon sx={{ color: '#8c7077' }} />
                                    </IconButton>

                                    <Chip label={selectedCake.category} size="small" sx={{ alignSelf: 'flex-start', bgcolor: '#fcf4f4', color: '#d46a89', fontWeight: 700, mb: 2, letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.75rem' }} />

                                    <Typography variant="h4" sx={{ mb: 1, lineHeight: 1.2, pr: 4 }}>
                                        {selectedCake.name}
                                    </Typography>

                                    <Typography variant="h5" sx={{ color: '#5c434a', mb: 3, fontFamily: '"Outfit", sans-serif', fontWeight: 800 }}>
                                        {(() => {
                                            let basePrice = parsePrice(selectedCake.price);
                                            if (selectedCake.sizes && selectedCake.sizes.length > 0) {
                                                basePrice += selectedCake.sizes[selectedSizeIndex]?.price || 0;
                                            }
                                            if (selectedCake.cardOptions && selectedCake.cardOptions.length > 0) {
                                                basePrice += selectedCake.cardOptions[selectedCardIndex]?.price || 0;
                                            }
                                            return `LKR.${basePrice.toFixed(2)}`;
                                        })()}
                                    </Typography>

                                    <Divider sx={{ borderColor: '#fcf4f4', mb: 3 }} />

                                    <Typography variant="body1" sx={{ color: '#8c7077', lineHeight: 1.8, mb: 4 }}>
                                        {selectedCake.description}
                                    </Typography>

                                    {/* Options Selectors */}
                                    {selectedCake.sizes && selectedCake.sizes.length > 0 && (
                                        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                            <InputLabel>SIZE (INCHES)</InputLabel>
                                            <Select
                                                value={selectedSizeIndex}
                                                label="SIZE (INCHES)"
                                                onChange={(e) => setSelectedSizeIndex(e.target.value)}
                                            >
                                                {selectedCake.sizes.map((size, idx) => (
                                                    <MenuItem key={idx} value={idx}>
                                                        {size.label} {size.price > 0 ? `(+LKR.${size.price})` : ''}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {selectedCake.cardOptions && selectedCake.cardOptions.length > 0 && (
                                        <FormControl fullWidth size="small" sx={{ mb: 4 }}>
                                            <InputLabel>Card Options (placed with the box)</InputLabel>
                                            <Select
                                                value={selectedCardIndex}
                                                label="Card Options (placed with the box)"
                                                onChange={(e) => setSelectedCardIndex(e.target.value)}
                                            >
                                                {selectedCake.cardOptions.map((card, idx) => (
                                                    <MenuItem key={idx} value={idx}>
                                                        {card.label} {card.price > 0 ? `(+LKR.${card.price})` : ''}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {/* Quantity + Add to Cart */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', background: '#fffafb', borderRadius: '30px', p: 0.5, border: '1px solid #fcf4f4' }}>
                                            <QtyButton size="small" onClick={() => setQuantity(q => Math.max(1, q - 1))}>
                                                <RemoveIcon fontSize="small" />
                                            </QtyButton>
                                            <Typography sx={{ px: 3, fontWeight: 700, color: '#5c434a', minWidth: '40px', textAlign: 'center' }}>
                                                {quantity}
                                            </Typography>
                                            <QtyButton size="small" onClick={() => setQuantity(q => q + 1)}>
                                                <AddIcon fontSize="small" />
                                            </QtyButton>
                                        </Box>

                                        <BakeryButton startIcon={<AddShoppingCartIcon />} onClick={handleAddToCart} sx={{ flexGrow: 1 }}>
                                            Add to Cart
                                        </BakeryButton>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContent>
                )}
            </Dialog>

            {/* Cart confirmation snackbar */}
            <Snackbar
                open={snack !== ''}
                message={snack}
                autoHideDuration={3000}
                onClose={() => setSnack('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                ContentProps={{
                    sx: { background: '#fff', color: '#d46a89', fontWeight: 'bold', border: '1px solid #f4a2bc', boxShadow: '0 10px 30px rgba(212, 106, 137, 0.3)' }
                }}
            />
        </ThemeProvider>
    );
};

export default Menu;
